import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SingleResponseModel } from '../../common/models/single-response.model';
import { PaginableResponseModel } from '../../common/models/paginable-response.model';

export interface ListBookingsFilter {
  from: string;
  to: string;
  page?: number;
  pageSize?: number;
  locationUids?: string[];
  staffUids?: string[];
}

export interface BookingDto {
  bookingId: string;
  merchantLocationId: string;
  merchantLocationConceptId: string;
  merchantStaffId: string;
  startAt: string;
  endAt: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  price?: number;
  statusBookingId?: string;
  
  // Populated fields for UI
  locationName?: string;
  conceptName?: string;
  staffName?: string;
}

export interface CreateBookingRequest {
  merchantLocationId: string;
  merchantLocationConceptId: string;
  merchantStaffId: string;
  startAt: string;
  /** Fin opcional con duración personalizada. Si se omite, el backend usa la duración del concepto. */
  endAt?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
}

export interface UpdateBookingRequest {
  bookingId: string;
  merchantLocationConceptId: string;
  merchantStaffId: string;
  startAt: string;
  /** Fin opcional con duración personalizada. Si se omite, el backend recalcula con la duración del concepto. */
  endAt?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
}

/**
 * Service for managing bookings in the new booking system
 */
@Injectable()
export class BookingService {
  private baseUrl = `${environment.apiUrl}/api/v2/booking/bookings`;

  constructor(private http: HttpClient) {}

  list(filter: ListBookingsFilter = {
    from: '',
    to: ''
  }): Observable<PaginableResponseModel<BookingDto>> {
    return this.http.post<PaginableResponseModel<BookingDto>>(`${this.baseUrl}/list`, filter);
  }

  getById(bookingId: string): Observable<SingleResponseModel<BookingDto>> {
    return this.http.get<SingleResponseModel<BookingDto>>(`${this.baseUrl}/${bookingId}`);
  }

  /**
   * Create a new booking
   */
  create(data: CreateBookingRequest): Observable<SingleResponseModel<string>> {
    return this.http.post<SingleResponseModel<string>>(this.baseUrl, data);
  }

  /**
   * Update an existing booking
   * NOTE: This endpoint may need to be implemented in the backend
   * Expected endpoint: PUT /api/v2/booking/bookings/{bookingId}
   */
  update(data: UpdateBookingRequest): Observable<SingleResponseModel<boolean>> {
    return this.http.put<SingleResponseModel<boolean>>(`${this.baseUrl}/${data.bookingId}`, data);
  }

  cancel(bookingId: string): Observable<SingleResponseModel<boolean>> {
    return this.http.post<SingleResponseModel<boolean>>(`${this.baseUrl}/${bookingId}/cancel`, {});
  }

  delete(bookingId: string): Observable<SingleResponseModel<boolean>> {
    return this.http.delete<SingleResponseModel<boolean>>(`${this.baseUrl}/${bookingId}`);
  }

  /**
   * Search bookings with filters
   */
  search(filters: {
    locationIds?: string[];
    staffIds?: string[];
    fromDate: string;
    toDate: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/search`, filters);
  }
}
